/**
 * Debug and testing utilities for cycling analysis validation.
 * Helps verify pedal cycle detection, angle measurements, and overall accuracy.
 */

import { calculateRobustStats } from './OutlierFilter';

/**
 * Log detailed information about pedal cycle detection.
 * @param {Object} pedalCycleData - Output from detectPedalCycles
 * @param {Array} frames - Original frame data
 */
export function debugPedalCycles(pedalCycleData, frames) {
  if (!pedalCycleData || !pedalCycleData.detected) {
    console.warn('⚠️ No pedal cycle data available');
    return;
  }

  console.group('🚴 Pedal Cycle Detection Debug');
  
  console.log('📊 Detection Summary:');
  console.log(`  Total cycles detected: ${pedalCycleData.totalCycles}`);
  console.log(`  BDC frames (troughs): ${pedalCycleData.validBDCFrames} - EXACT frames only`);
  console.log(`  TDC frames (peaks): ${pedalCycleData.validTDCFrames} - EXACT frames only`);
  
  if (pedalCycleData.peakIndices && pedalCycleData.peakIndices.length > 0) {
    console.log('\n📍 Peak Indices (TDC - Top Dead Center):');
    console.log(`  Frames: [${pedalCycleData.peakIndices.join(', ')}]`);
    
    // Show knee angles at peaks (should be highest flexion)
    const kneeAtPeaks = pedalCycleData.peakIndices
      .map(idx => frames[idx]?.angles?.knee)
      .filter(v => v !== undefined);
    if (kneeAtPeaks.length > 0) {
      console.log(`  Knee angles at TDC: [${kneeAtPeaks.map(v => v.toFixed(1)).join('°, ')}°]`);
      console.log(`  Mean: ${(kneeAtPeaks.reduce((a,b) => a+b) / kneeAtPeaks.length).toFixed(1)}°`);
    }
  }
  
  if (pedalCycleData.troughIndices && pedalCycleData.troughIndices.length > 0) {
    console.log('\n📍 Trough Indices (BDC - Bottom Dead Center):');
    console.log(`  Frames: [${pedalCycleData.troughIndices.join(', ')}]`);
    
    // Show knee angles at troughs (should be lowest flexion / max extension)
    const kneeAtTroughs = pedalCycleData.troughIndices
      .map(idx => frames[idx]?.angles?.knee)
      .filter(v => v !== undefined);
    if (kneeAtTroughs.length > 0) {
      console.log(`  Knee angles at BDC: [${kneeAtTroughs.map(v => v.toFixed(1)).join('°, ')}°]`);
      console.log(`  Mean: ${(kneeAtTroughs.reduce((a,b) => a+b) / kneeAtTroughs.length).toFixed(1)}°`);
      const stats = calculateRobustStats(kneeAtTroughs);
      if (stats) {
        console.log(`  Std dev: ${stats.std.toFixed(1)}° (should be < 5° for consistent pedaling)`);
      }
    }
  }
  
  // Validate cycle spacing (should be relatively consistent)
  if (pedalCycleData.peakIndices && pedalCycleData.peakIndices.length >= 2) {
    const spacing = [];
    for (let i = 1; i < pedalCycleData.peakIndices.length; i++) {
      spacing.push(pedalCycleData.peakIndices[i] - pedalCycleData.peakIndices[i-1]);
    }
    console.log('\n⏱️ Cycle Spacing (frames between peaks):');
    console.log(`  Intervals: [${spacing.join(', ')}]`);
    const avgSpacing = spacing.reduce((a,b) => a+b) / spacing.length;
    console.log(`  Average: ${avgSpacing.toFixed(1)} frames`);
    const expectedCadence = (30 / (avgSpacing / 30)) * 60; // Assuming 30fps
    console.log(`  Estimated cadence: ${expectedCadence.toFixed(0)} RPM`);
  }
  
  console.groupEnd();
}

/**
 * Validate measurement consistency across cycles.
 * @param {Object} results - Analysis results
 * @returns {Object} - Validation report
 */
export function validateMeasurementConsistency(results) {
  const report = {
    timestamp: new Date().toISOString(),
    tests: [],
    overallPass: true,
  };
  
  // Test 1: Knee angle consistency at BDC
  if (results.angles?.knee?.valid && results.angles.knee.method === 'pedal-position-based') {
    const kneeTest = {
      name: 'Knee Angle Consistency at BDC',
      measured: results.angles.knee.evaluatedValue,
      range: `${results.angles.knee.min}° - ${results.angles.knee.max}°`,
      samplesUsed: results.angles.knee.samplesUsed,
      outliersRemoved: results.angles.knee.outliersRemoved || 0,
      pass: true,
      notes: [],
    };
    
    // Check if measurement is in reasonable range
    if (results.angles.knee.evaluatedValue < 25 || results.angles.knee.evaluatedValue > 60) {
      kneeTest.pass = false;
      kneeTest.notes.push(`❌ Knee angle ${results.angles.knee.evaluatedValue}° outside expected range (25-60°)`);
      report.overallPass = false;
    } else {
      kneeTest.notes.push(`✓ Knee angle ${results.angles.knee.evaluatedValue}° within expected range`);
    }
    
    // Check sample count
    if (results.angles.knee.samplesUsed < 3) {
      kneeTest.pass = false;
      kneeTest.notes.push(`❌ Only ${results.angles.knee.samplesUsed} samples used (need ≥3)`);
      report.overallPass = false;
    } else {
      kneeTest.notes.push(`✓ Used ${results.angles.knee.samplesUsed} samples`);
    }
    
    report.tests.push(kneeTest);
  }
  
  // Test 2: Hip angle consistency at TDC
  if (results.angles?.hip?.valid && results.angles.hip.method === 'pedal-position-based') {
    const hipTest = {
      name: 'Hip Angle Consistency at TDC',
      measured: results.angles.hip.evaluatedValue,
      range: `${results.angles.hip.min}° - ${results.angles.hip.max}°`,
      samplesUsed: results.angles.hip.samplesUsed,
      outliersRemoved: results.angles.hip.outliersRemoved || 0,
      pass: true,
      notes: [],
    };
    
    // Check if measurement is in reasonable range
    if (results.angles.hip.evaluatedValue < 40 || results.angles.hip.evaluatedValue > 120) {
      hipTest.pass = false;
      hipTest.notes.push(`❌ Hip angle ${results.angles.hip.evaluatedValue}° outside expected range (40-120°)`);
      report.overallPass = false;
    } else {
      hipTest.notes.push(`✓ Hip angle ${results.angles.hip.evaluatedValue}° within expected range`);
    }
    
    // Check sample count
    if (results.angles.hip.samplesUsed < 3) {
      hipTest.pass = false;
      hipTest.notes.push(`❌ Only ${results.angles.hip.samplesUsed} samples used (need ≥3)`);
      report.overallPass = false;
    } else {
      hipTest.notes.push(`✓ Used ${results.angles.hip.samplesUsed} samples`);
    }
    
    report.tests.push(hipTest);
  }
  
  // Test 3: Pedal cycle detection
  if (results.pedalCycles) {
    const cycleTest = {
      name: 'Pedal Cycle Detection',
      detected: results.pedalCycles.detected,
      totalCycles: results.pedalCycles.totalCycles || 0,
      pass: true,
      notes: [],
    };
    
    if (!results.pedalCycles.detected) {
      cycleTest.pass = false;
      cycleTest.notes.push(`❌ Pedal cycles not detected: ${results.pedalCycles.reason || 'unknown reason'}`);
      report.overallPass = false;
    } else if (results.pedalCycles.totalCycles < 3) {
      cycleTest.pass = false;
      cycleTest.notes.push(`❌ Only ${results.pedalCycles.totalCycles} cycles detected (need ≥3)`);
      report.overallPass = false;
    } else {
      cycleTest.notes.push(`✓ Detected ${results.pedalCycles.totalCycles} valid cycles`);
    }
    
    report.tests.push(cycleTest);
  }
  
  // Test 4: Data quality
  const qualityTest = {
    name: 'Overall Data Quality',
    accuracy: results.accuracy,
    stableFrames: results.stableFrameCount,
    totalFrames: results.totalFramesProcessed,
    pass: true,
    notes: [],
  };
  
  if (results.accuracy === 'insufficient' || results.accuracy === 'low') {
    qualityTest.pass = false;
    qualityTest.notes.push(`❌ Accuracy too low: ${results.accuracy}`);
    report.overallPass = false;
  } else {
    qualityTest.notes.push(`✓ Accuracy: ${results.accuracy}`);
  }
  
  if (results.stableFrameCount < 30) {
    qualityTest.pass = false;
    qualityTest.notes.push(`❌ Only ${results.stableFrameCount} stable frames (need ≥30)`);
    report.overallPass = false;
  } else {
    qualityTest.notes.push(`✓ ${results.stableFrameCount} stable frames collected`);
  }
  
  report.tests.push(qualityTest);
  
  return report;
}

/**
 * Print validation report to console.
 * @param {Object} report - Validation report from validateMeasurementConsistency
 */
export function printValidationReport(report) {
  console.group('🧪 Validation Report');
  console.log(`Generated: ${report.timestamp}`);
  console.log(`Overall: ${report.overallPass ? '✅ PASS' : '❌ FAIL'}\n`);
  
  report.tests.forEach(test => {
    const icon = test.pass ? '✅' : '❌';
    console.group(`${icon} ${test.name}`);
    
    Object.keys(test).forEach(key => {
      if (key !== 'name' && key !== 'pass' && key !== 'notes') {
        console.log(`  ${key}: ${test[key]}`);
      }
    });
    
    if (test.notes && test.notes.length > 0) {
      console.log('  Details:');
      test.notes.forEach(note => console.log(`    ${note}`));
    }
    
    console.groupEnd();
  });
  
  console.groupEnd();
}

/**
 * Generate a CSV string of knee angles over time for plotting.
 * @param {Array} frames - Frame data
 * @param {Object} pedalCycleData - Pedal cycle detection results
 * @returns {string} - CSV format: frameIndex,timestamp,kneeAngle,isPeak,isTrough
 */
export function exportKneeAnglePlotData(frames, pedalCycleData) {
  let csv = 'frameIndex,timestamp,kneeAngle,isPeak,isTrough,smoothedKnee\n';
  
  frames.forEach((frame, idx) => {
    const isPeak = pedalCycleData?.peakIndices?.includes(idx) ? 1 : 0;
    const isTrough = pedalCycleData?.troughIndices?.includes(idx) ? 1 : 0;
    const smoothed = pedalCycleData?.smoothedKnee?.[idx] || frame.angles.knee;
    
    csv += `${idx},${frame.timestamp},${frame.angles.knee},${isPeak},${isTrough},${smoothed}\n`;
  });
  
  return csv;
}

/**
 * Compare old (mean-based) vs new (pedal-position-based) measurements.
 * @param {Array} frames - Frame data
 * @param {Object} newResults - Results with pedal position detection
 * @returns {Object} - Comparison report
 */
export function compareOldVsNew(frames, newResults) {
  // Calculate old-style (mean) measurements
  const kneeAngles = frames.map(f => f.angles.knee).filter(v => v !== undefined && !isNaN(v));
  const hipAngles = frames.map(f => f.angles.hip).filter(v => v !== undefined && !isNaN(v));
  
  const oldKneeMean = kneeAngles.reduce((a, b) => a + b, 0) / kneeAngles.length;
  const oldKneeMin = Math.min(...kneeAngles);
  const oldHipMean = hipAngles.reduce((a, b) => a + b, 0) / hipAngles.length;
  const oldHipMin = Math.min(...hipAngles);
  
  const comparison = {
    knee: {
      old: {
        method: 'mean across all frames',
        value: oldKneeMean.toFixed(1),
        minValue: oldKneeMin.toFixed(1),
      },
      new: {
        method: newResults.angles.knee.method || 'unknown',
        value: newResults.angles.knee.evaluatedValue.toFixed(1),
        measurementPosition: newResults.angles.knee.measurementPosition,
        samplesUsed: newResults.angles.knee.samplesUsed,
        outliersRemoved: newResults.angles.knee.outliersRemoved || 0,
      },
      difference: Math.abs(oldKneeMean - newResults.angles.knee.evaluatedValue).toFixed(1),
      improvement: null,
    },
    hip: {
      old: {
        method: 'mean across all frames',
        value: oldHipMean.toFixed(1),
        minValue: oldHipMin.toFixed(1),
      },
      new: {
        method: newResults.angles.hip.method || 'unknown',
        value: newResults.angles.hip.evaluatedValue.toFixed(1),
        measurementPosition: newResults.angles.hip.measurementPosition,
        samplesUsed: newResults.angles.hip.samplesUsed,
        outliersRemoved: newResults.angles.hip.outliersRemoved || 0,
      },
      difference: Math.abs(oldHipMean - newResults.angles.hip.evaluatedValue).toFixed(1),
      improvement: null,
    },
  };
  
  // Determine if new method is an improvement
  comparison.knee.improvement = newResults.angles.knee.method === 'pedal-position-based' && 
                                 newResults.angles.knee.samplesUsed >= 3;
  comparison.hip.improvement = newResults.angles.hip.method === 'pedal-position-based' && 
                               newResults.angles.hip.samplesUsed >= 3;
  
  return comparison;
}

/**
 * Print comparison report to console.
 * @param {Object} comparison - Output from compareOldVsNew
 */
export function printComparison(comparison) {
  console.group('📊 Old vs New Measurement Comparison');
  
  console.group('🦵 Knee Angle');
  console.log(`Old method: ${comparison.knee.old.method}`);
  console.log(`  Value: ${comparison.knee.old.value}°`);
  console.log(`  Min value (used as estimate): ${comparison.knee.old.minValue}°`);
  console.log(`\nNew method: ${comparison.knee.new.method}`);
  console.log(`  Value: ${comparison.knee.new.value}°`);
  console.log(`  Measured at: ${comparison.knee.new.measurementPosition}`);
  console.log(`  Samples used: ${comparison.knee.new.samplesUsed}`);
  console.log(`  Outliers removed: ${comparison.knee.new.outliersRemoved}`);
  console.log(`\nDifference: ${comparison.knee.difference}°`);
  console.log(`Improvement: ${comparison.knee.improvement ? '✅ Yes' : '❌ No'}`);
  console.groupEnd();
  
  console.group('🦿 Hip Angle');
  console.log(`Old method: ${comparison.hip.old.method}`);
  console.log(`  Value: ${comparison.hip.old.value}°`);
  console.log(`  Min value (used as estimate): ${comparison.hip.old.minValue}°`);
  console.log(`\nNew method: ${comparison.hip.new.method}`);
  console.log(`  Value: ${comparison.hip.new.value}°`);
  console.log(`  Measured at: ${comparison.hip.new.measurementPosition}`);
  console.log(`  Samples used: ${comparison.hip.new.samplesUsed}`);
  console.log(`  Outliers removed: ${comparison.hip.new.outliersRemoved}`);
  console.log(`\nDifference: ${comparison.hip.difference}°`);
  console.log(`Improvement: ${comparison.hip.improvement ? '✅ Yes' : '❌ No'}`);
  console.groupEnd();
  
  console.groupEnd();
}

/**
 * Enable all debug logging for a complete analysis run.
 * Call this before starting analysis to get comprehensive debug output.
 */
export function enableDebugMode() {
  window.__CYCLING_DEBUG__ = true;
  console.log('🔍 Cycling Analysis Debug Mode ENABLED');
  console.log('Console will show detailed information about:');
  console.log('  - Pedal cycle detection');
  console.log('  - Angle measurements');
  console.log('  - Validation results');
  console.log('  - Old vs new comparisons');
}

/**
 * Disable debug logging.
 */
export function disableDebugMode() {
  window.__CYCLING_DEBUG__ = false;
  console.log('🔍 Cycling Analysis Debug Mode DISABLED');
}
